<div
  class="event-map event-map--rows p-2 gap-2"
  [class.event-map--split]="layoutWide()"
  [class.event-map--stack]="!layoutWide()"
  [class.event-map--drawer-open]="!layoutWide() && drawerExpanded()"
>
  <aside class="event-map__list">
    <button
      type="button"
      class="event-map__drawer-handle"
      [attr.aria-expanded]="drawerExpanded()"
      [attr.aria-label]="'events' | translate"
      (pointerdown)="onDrawerPointerDown($event)"
      (pointermove)="onDrawerPointerMove($event)"
      (pointerup)="onDrawerPointerUp()"
      (pointercancel)="onDrawerPointerUp()"
      (click)="onDrawerToggle()"
    >
      <span class="event-map__drawer-grip" aria-hidden="true"></span>
      @if (!layoutWide() && !drawerExpanded()) {
        <span class="event-map__drawer-count fs-14 fw-semibold">
          {{ listPins().length }}
          @if (listPins().length === 1) {
            {{ 'event' | translate }}
          } @else {
            {{ 'events' | translate }}
          }
        </span>
      }
    </button>
    <div
      class="event-map__cards event-map__cards--rows p-2"
      (scroll)="onListScroll($event)"
    >
      @for (pin of listPins(); track pin.card.id) {
          <button
            type="button"
            class="event-map-row d-flex gap-2 p-2"
            [class.event-map-row--active]="selectedId() === pin.card.id"
            [class.event-map-row--hover]="hoveredId() === pin.card.id"
            (click)="onCardActivate(pin)"
            (mouseenter)="hoverPin(pin.card.id)"
            (mouseleave)="hoverPin(null)"
          >
            <img
              class="event-map-row__thumb"
              [src]="pin.card.thumb"
              alt=""
            />
            <div class="event-map-row__body min-w-0 d-flex flex-column gap-1">
              <p class="event-map-row__title fs-14 fw-semibold mb-0">{{ pin.card.name | capitalizeTitle }}</p>
              @if (pin.distanceKm != null) {
                <p class="event-map-row__meta fs-12 fw-normal mb-0">{{ pin.distanceKm }} km</p>
              }
              @if (pin.card.city) {
                <p class="event-map-row__meta fs-12 fw-normal mb-0">{{ pin.card.city }}</p>
              }
              <p class="event-map-row__meta fs-11 fw-normal mb-0">{{ pin.card.dates }}</p>
              <span class="event-map-row__cta fs-12 fw-semibold">{{ pin.card.cta }}</span>
            </div>
          </button>
      }
      @if (listPins().length === 0 && !isLoading()) {
        <stupa-empty-state
          class="event-map__empty-list"
          purpose="no-results"
          [description]="'zoom_out_to_see_events' | translate"
        />
      }
      @if (isLoading()) {
        <stupa-skeleton-loader class="event-map__loading" [rows]="3" [columns]="1" />
      }
    </div>
  </aside>

  <div
    #mapPane
    class="event-map__pane"
    (pointerdown)="onPointerDown($event)"
    (pointermove)="onPointerMove($event)"
    (pointerup)="onPointerUp($event)"
    (pointercancel)="onPointerUp($event)"
    (dblclick)="onDoubleClick($event)"
  >
    <div class="event-map__tiles" [style.scale]="tileScale()">
      @for (tile of tiles(); track tile.key) {
        <img class="event-map__tile" [src]="tile.url" alt="" [style.left.px]="tile.left" [style.top.px]="tile.top" />
      }
    </div>

    @for (cluster of clusters(); track cluster.id) {
      <button
        type="button"
        [class]="clusterPinClass(cluster.memberIds)"
        [style]="pinStyle(cluster.point)"
        (click)="onClusterClick(cluster.memberIds)"
        (mouseenter)="hoverPin(cluster.memberIds[0] ?? null)"
        (mouseleave)="hoverPin(null)"
        [attr.aria-label]="cluster.memberIds.length > 1 ? ('' + cluster.memberIds.length) : clusterCaption(cluster.memberIds)"
      >
        @if (cluster.memberIds.length > 1) {
          <span class="event-map-pin__count fs-10 fw-bold">{{ cluster.memberIds.length }}</span>
        }
        @if (origin() && cluster.memberIds.length === 1) {
          <span class="event-map-label fs-11 fw-medium px-2 py-1">{{ clusterCaption(cluster.memberIds) }}</span>
        }
      </button>
    }

    @if (youAreHere(); as you) {
      <div class="event-map-you" [style]="pinStyle(you)">
        <span class="event-map-you__dot"></span>
        <span class="event-map-you__label fs-12 fw-semibold px-2 py-1">{{ 'you' | translate }}</span>
      </div>
    }

    @if (emptyViewport()) {
      <button type="button" class="event-map__empty fs-13 fw-medium" (click)="resetView()">
        {{ 'zoom_out_to_see_events' | translate }}
      </button>
    }

    @if (locatedPins().length === 0 && pins().length > 0) {
      <p class="event-map__empty fs-13 fw-medium mb-0">{{ 'no_event_locations' | translate }}</p>
    }

    @if (selectedPin(); as pop) {
      @if (pop.point && (layoutWide() || !drawerExpanded())) {
        <div
          class="event-map-pop"
          [class.event-map-pop--docked]="!layoutWide()"
          [style]="layoutWide() ? pinStyle(pop.point) : null"
          (pointerdown)="$event.stopPropagation()"
        >
          <div
            class="event-map-pop__thumb"
            [style.background-image]="pop.card.thumb ? 'url(' + pop.card.thumb + ')' : 'none'"
          ></div>
          <div class="event-map-pop__body p-2">
            <p class="event-map-card__title fs-14 fw-semibold mb-0">{{ pop.card.name }}</p>
            @if (pop.distanceKm != null) {
              <p class="event-map-card__city fs-12 fw-normal mb-0">{{ pop.distanceKm }} km</p>
            }
            @if (pop.card.city) {
              <p class="event-map-card__city fs-12 fw-normal mb-0">{{ pop.card.city }}</p>
            }
            <p class="event-map-card__meta fs-11 fw-normal mb-1">{{ pop.card.dates }}</p>
            <button type="button" class="event-map-pop__cta fs-13 fw-medium w-100" (click)="onCta(pop)">
              {{ pop.card.cta }}
            </button>
          </div>
        </div>
      }
    }

    <div class="event-map__legend p-2">
      <p class="event-map__legend-title fs-10 fw-bold mb-1">{{ 'event_status' | translate }}</p>
      @for (item of legend; track item.value) {
        <div class="event-map__legend-row fs-12 fw-medium d-flex align-items-center gap-1">
          <span class="event-map__dot" [class]="'event-map__dot is-' + toneClass(item.value)"></span>
          {{ item.class_name }}
        </div>
      }
    </div>

    <div class="event-map__controls d-flex flex-column gap-1">
      <button type="button" class="event-map__ctrl" (click)="zoomBy(1)" [attr.aria-label]="'zoom_in' | translate">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
      </button>
      <button type="button" class="event-map__ctrl" (click)="zoomBy(-1)" [attr.aria-label]="'zoom_out' | translate">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M5 12h14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
      </button>
      <button
        type="button"
        class="event-map__ctrl"
        [disabled]="locating()"
        [attr.aria-busy]="locating()"
        [attr.aria-label]="'near_me' | translate"
        (click)="locateUser()"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.8" />
          <path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
      </button>
    </div>

    <div class="event-map__scale" [attr.aria-label]="scaleBar().label">
      <span class="event-map__scale-label fs-11 fw-medium">{{ scaleBar().label }}</span>
      <span class="event-map__scale-bar" [style.width.px]="scaleBar().widthPx"></span>
    </div>

    <p class="event-map__attr fs-8 fw-medium mb-0">© Esri, HERE, Garmin, OpenStreetMap</p>
  </div>
</div>
